import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Search, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, Empty } from "@/components/common";
import { DocUploadDialog } from "@/components/docs/DocUploadDialog";
import { api, errMsg } from "@/lib/api";
import { fmtD } from "@/lib/format";

export const useDocsMeta = () => {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    api.get("/documents/meta").then((r) => setMeta(r.data));
  }, []);
  return meta;
};

const SearchBox = () => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const go = async (e) => {
    e.preventDefault();
    try {
      setErr("");
      setRes((await api.get("/documents/search", { params: { q } })).data.results);
    } catch (x) {
      setErr(errMsg(x));
    }
  };
  return (
    <section className="card-soft mb-5 p-5" data-testid="doc-search">
      <form onSubmit={go} className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nei documenti (es. reperibilità, esumazioni, penali)…" className="rounded-xl" data-testid="doc-search-input" />
        <Button type="submit" className="rounded-xl" data-testid="doc-search-submit"><Search className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Cerca</span></Button>
      </form>
      {err && <p className="mt-2 text-sm text-red-600">{err}</p>}
      {res && (
        <div className="mt-4 space-y-2" data-testid="doc-search-results">
          {res.length === 0 && <p className="text-sm text-muted-foreground">Nessun risultato.</p>}
          {res.map((r, i) => (
            <Link key={`${r.doc_id}-${r.page}`} to={`/documenti/${r.doc_id}?page=${r.page}`} className="block rounded-xl border bg-white p-3 hover:bg-accent/40" data-testid={`doc-search-result-${i}`}>
              <p className="text-sm font-bold">{r.title} · <span className="text-primary">p. {r.page}</span></p>
              <p className="mt-1 text-xs text-muted-foreground">{r.snippet}</p>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

export default function Documenti() {
  const meta = useDocsMeta();
  const [docs, setDocs] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    api.get("/documents").then((r) => setDocs(r.data));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  return (
    <div data-testid="documenti-page">
      <PageHeader title="Documenti contrattuali" subtitle="Capitolati, offerte e allegati per commessa · ricerca con pagina esatta"
        actions={meta?.can_upload && <Button onClick={() => setOpen(true)} className="h-11 rounded-xl font-bold" data-testid="doc-upload-button"><Upload className="mr-1 h-4 w-4" />Carica PDF</Button>} />
      <SearchBox />
      {!docs ? <p className="text-muted-foreground">Caricamento…</p> : docs.length === 0 ? <Empty text="Nessun documento caricato." testid="docs-empty" /> : (
        <div className="grid gap-3 md:grid-cols-2" data-testid="docs-list">
          {docs.map((d) => (
            <Link key={d.id} to={`/documenti/${d.id}`} className="card-soft flex gap-4 p-5 hover:bg-accent/30" data-testid={`doc-card-${d.id}`}>
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent text-primary"><FileText className="h-6 w-6" /></div>
              <div className="min-w-0">
                <p className="font-bold">{d.title}</p>
                <p className="text-xs text-muted-foreground">{d.commessa_code} · {meta?.types[d.doc_type]} · v{d.version} · {d.pages} pagine · {fmtD(d.created_at)}</p>
                <p className="mt-1 text-xs font-semibold text-primary">{d.obligations} obblighi estratti</p>
              </div>
            </Link>
          ))}
        </div>
      )}
      {meta && <DocUploadDialog open={open} onOpenChange={setOpen} types={meta.types} onDone={load} />}
    </div>
  );
}
